import {
  useLoaderData,
  useNavigation,
  redirect,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { DomainsTable } from "@/components/dashboard/DomainsTable/DomainsTable";
import { CustomizeColumns } from "@/components/dashboard/CustomizeColumns/CustomizeColumns";
import { useColumnVisibility } from "@/components/dashboard/CustomizeColumns/useColumnVisibility";
import { DashboardDistributions } from "@/components/dashboard/Distributions";
import { DashboardFilters } from "@/components/dashboard/Filters/DashboardFilters";
import { DashboardStats } from "@/components/dashboard/DashboardStats";

import { dashboardService } from "@/services/DashboardService";
import { useDashboardFilters } from "@/hooks/useDashboardFilters";
import {
  getDashboardQueryKey,
  useDashboardData,
} from "@/hooks/useDashboardData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { queryClient } from "@/lib/queryClient";
import { fetchUserProfile } from "@/hooks/useUser";
import {
  EMPTY_DASHBOARD_DISTRIBUTIONS,
  EMPTY_DASHBOARD_STATS,
  EMPTY_DOMAINS,
  parseDashboardFiltersFromUrl,
  type DashboardLoaderData,
} from "@/types/dashboard";

const DEFAULT_PAGE = 1;

export async function dashboardLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const pageParam = Number(url.searchParams.get("page") || DEFAULT_PAGE);
  const limitParam = Number(url.searchParams.get("limit") || "15");
  const filters = parseDashboardFiltersFromUrl(url);

  const page =
    Number.isNaN(pageParam) || pageParam < 1 ? DEFAULT_PAGE : pageParam;
  const limit = dashboardService.normalizeLimit(limitParam);

  try {
    await queryClient.prefetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: 1000 * 60 * 5,
    });

    await queryClient.prefetchQuery({
      queryKey: getDashboardQueryKey(page, filters, limit),
      queryFn: () => dashboardService.getDashboardData(page, filters, limit),
      staleTime: 1000 * 60 * 5,
    });

    return { page, limit, filters };
  } catch {
    return redirect("/login");
  }
}

export default function Dashboard() {
  const loaderParams = useLoaderData() as DashboardLoaderData;
  const navigation = useNavigation();

  const { visibleColumns, columnOrder, toggleColumn, reorderColumns } =
    useColumnVisibility();

  const {
    initialFilters,
    filtersOpen,
    setFiltersOpen,
    handleApplyFilters,
    handlePageChange,
    handleLimitChange,
    page: urlPage,
    limit: urlLimit,
  } = useDashboardFilters();

  const page = urlPage || loaderParams.page;
  const limit = dashboardService.normalizeLimit(urlLimit || loaderParams.limit);

  const { data, isPending } = useDashboardData({
    page,
    filters: loaderParams.filters,
    limit,
  });

  const domains = data?.domains || EMPTY_DOMAINS;
  const totalCount = data?.totalCount || 0;
  const stats = data?.stats ?? EMPTY_DASHBOARD_STATS;
  const distributions = data?.distributions ?? EMPTY_DASHBOARD_DISTRIBUTIONS;

  const isRouteLoading = navigation.state === "loading";
  const isLoadingData = isRouteLoading || isPending;

  const totalPages = Math.ceil(totalCount / limit);
  const allowedLimits = dashboardService.getAllowedLimits();

  return (
    <div className="space-y-8 h-full flex flex-col">
      {/* HEADER */}
      <div className="flex-none flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Dashboard
        </h1>
      </div>
      {/* STATS */}
      <div className="flex-none">
        <DashboardStats stats={stats} />
      </div>
      {/* DISTRIBUTIONS */}
      <div className="flex-none space-y-6">
        <DashboardDistributions data={distributions} />
      </div>
      {/* FILTERS & COLUMN CUSTOMIZATION */}
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "cursor-pointer gap-2 border-border",
            filtersOpen && "border-primary/40 bg-accent",
          )}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Filter className="h-4 w-4" />
          Filters
        </Button>

        <div className="flex items-center gap-2">
          <CustomizeColumns
            visibleColumns={visibleColumns}
            columnOrder={columnOrder}
            onToggleColumn={toggleColumn}
            onReorderColumns={reorderColumns}
          />
        </div>
      </div>
      {filtersOpen && (
        <div className="flex-none animate-in fade-in slide-in-from-top-2 duration-200">
          <DashboardFilters
            initialFilters={initialFilters}
            onApply={(f) => handleApplyFilters(f, true)}
            onCancel={() => setFiltersOpen(false)}
          />
        </div>
      )}
      <div
        className={cn(
          "relative min-h-0 w-full flex-1 overflow-hidden rounded-lg border border-border bg-card transition-opacity",
          isLoadingData && "opacity-50 pointer-events-none",
        )}
      >
        <DomainsTable
          domains={domains}
          className="h-full border-0 shadow-none rounded-none"
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
          scrollResetKey={`${page}-${limit}`}
        />
      </div>
      {totalCount > 0 && (
        <div className="flex-none flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-[80px] h-9">
                <SelectValue placeholder="15" />
              </SelectTrigger>
              <SelectContent>
                {allowedLimits.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">rows</span>
          </div>

          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
